import { Alert, Box, Tooltip, Typography } from '@mui/material'
import {
  AlertCircleIcon,
  ArrowNarrowRightIcon,
  Cube01Icon,
  File05Icon,
  File07Icon,
  HomeSmileIcon,
  InfoCircleIcon,
  Speedometer04Icon,
  WavesIcon,
} from '@untitled-theme/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { auth } from '@/app/config/firebase'
import { FireIcon } from '@/assets/icons'
import { useRetrofitFinancing } from '@/shared/api'
import Button from '@/shared/components/Button'
import EditISFPDataPopup from '@/shared/components/EditISFPDataPopup'
import { Energieklasse } from '@/shared/components/Energieklasse'
import HamburgerMenu from '@/shared/components/HamburgerMenu'
import { EnergyConsumptionInfoPopup, EnergyDemandInfoPopup } from '@/shared/components/InfoPopup'
import MeasureDetailsPopup from '@/shared/components/MeasureDetailsPopup'
import { PropertyValueIncrease } from '@/shared/components/PropertyValueIncrease'
import { SavingsPotential } from '@/shared/components/SavingsPotential'
import TextButton from '@/shared/components/TextButton'
import { useAssessment, useBuildingInfo, useCustomPackage } from '@/shared/hooks'
import type { RetrofitName } from '@/shared/types'
import type { EnergySource } from '@/shared/types/isfp.types'
import { getEnergySourceLabel } from '@/shared/utils/isfpEnumLabels'
import { localStorage } from '@/shared/utils/localStorage'

import { CurrentStateSection } from '../components/CurrentStateSection'
import { PhotoUploader } from '../components/PhotoUploader'
import { ProposedMeasures } from '../components/ProposedMeasures'
import { exportCurrentRatingToPDF } from '../utils/pdfExporter'

const CurrentRatingPage = () => {
  const navigate = useNavigate()
  const { buildingUuid } = useParams<{ buildingUuid: string }>()

  const [authenticatedImageUrl, setAuthenticatedImageUrl] = useState<string | null>(null)
  const [selectedMeasure, setSelectedMeasure] = useState<string | null>(null)
  const [isMeasureDetailsPopupOpen, setIsMeasureDetailsPopupOpen] = useState(false)
  const [hasOpenedMeasureDetailsPopup, setHasOpenedMeasureDetailsPopup] = useState(false)
  const [isEnergyDemandInfoPopupOpen, setIsEnergyDemandInfoPopupOpen] = useState(false)
  const [isEnergyConsumptionInfoPopupOpen, setIsEnergyConsumptionInfoPopupOpen] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)

  const { buildingInfo } = useBuildingInfo(buildingUuid)

  const formatEnergySourceLabel = (value: string | undefined) => {
    if (!value) return '–'
    return value
      .split(', ')
      .map(source => getEnergySourceLabel(source.trim() as EnergySource))
      .join(', ')
  }

  const { assessment } = useAssessment(buildingUuid)

  const {
    data: { demandBasedAssessment, consumptionBasedAssessment },
    mainPackageId,
    status,
  } = assessment || { data: {} }

  const { customPackage } = useCustomPackage(buildingUuid, mainPackageId)

  const returnTo = localStorage.getReturnTo()
  const showEndConsultationButton = !!customPackage && !!returnTo

  const currentRating = demandBasedAssessment?.currentState.energetics.energyLabel || 'E'

  const annualEnergyDemand = useMemo(
    () =>
      demandBasedAssessment?.currentState.energetics.annualFinalEnergyInKwh.total?.toLocaleString(
        'de-DE',
        { maximumFractionDigits: 0 }
      ),
    [demandBasedAssessment?.currentState.energetics.annualFinalEnergyInKwh.total]
  )

  const annualEnergyConsumption = useMemo(
    () =>
      consumptionBasedAssessment?.currentState.energetics.annualFinalEnergyInKwh.total?.toLocaleString(
        'de-DE',
        { maximumFractionDigits: 0 }
      ),
    [consumptionBasedAssessment?.currentState.energetics.annualFinalEnergyInKwh.total]
  )

  const fallbackImageUrl =
    'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=500&h=300&fit=crop'

  const buildingImageUrl = useMemo(() => {
    const photoUrl = buildingInfo?.photoUrls?.[0]
    if (!photoUrl) return undefined

    // Handle new relative URLs from eb-hub-service
    if (photoUrl.startsWith('/eb-hub/v1')) {
      if (import.meta.env.DEV) {
        return `/eb-hub-service${photoUrl}`
      }
      return `https://eb-hub-service.enter.de${photoUrl}`
    }

    // Replace /customer/building/ with /common/
    return photoUrl
      .replace('/customer/building/', '/common/')
      .replace('prod.enter.de', 'scout-service-v2.enter.de')
  }, [buildingInfo?.photoUrls])

  const handleMeasureClick = (retrofitName: RetrofitName) => {
    setSelectedMeasure(retrofitName)
    setIsMeasureDetailsPopupOpen(true)
    setHasOpenedMeasureDetailsPopup(true)
  }

  // Fetch retrofit financing data only when popup has been opened for the first time
  const { data: financing } = useRetrofitFinancing(buildingUuid, {
    enabled: hasOpenedMeasureDetailsPopup,
  })

  const handleExportPDF = async () => {
    if (!customPackage || !buildingUuid) return

    try {
      await exportCurrentRatingToPDF({
        buildingInfo,
        currentRating,
        potentialRating: customPackage.data.demandBasedCalculation.energetics.energyLabel,
        annualEnergyDemand,
        annualEnergyConsumption,
        customPackage,
        buildingImageUrl: authenticatedImageUrl || undefined,
        substance: buildingInfo?.substance,
        currentStateDescription: buildingInfo?.currentStateDescription,
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
    }
  }

  const handleNavigateToMeasures = () => {
    navigate(`/${buildingUuid}/measures-overview`)
  }

  const handleEndConsultation = () => {
    if (showEndConsultationButton) {
      const url = returnTo.replace(':buildingUuid', buildingUuid!)
      window.location.href = url
    }
  }

  useEffect(() => {
    let isMounted = true

    if (buildingImageUrl) {
      const fetchImage = async () => {
        try {
          const { currentUser } = auth
          if (!currentUser) {
            setAuthenticatedImageUrl(null)
            return
          }

          const token = await currentUser.getIdToken()
          const response = await fetch(buildingImageUrl, {
            headers: {
              'x-id-token': token,
              authorization: `Bearer ${token}`,
            },
          })

          if (!response.ok) {
            setAuthenticatedImageUrl(null)
            return
          }

          const blob = await response.blob()

          // Convert blob to base64
          const reader = new FileReader()
          reader.onloadend = () => {
            if (isMounted && typeof reader.result === 'string') {
              setAuthenticatedImageUrl(reader.result)
            }
          }
          reader.readAsDataURL(blob)
        } catch (error) {
          console.error('Failed to fetch building image:', error)
          if (isMounted) {
            setAuthenticatedImageUrl(null)
          }
        }
      }

      void fetchImage()
    } else {
      setAuthenticatedImageUrl(null)
    }

    return () => {
      isMounted = false
    }
  }, [buildingImageUrl])

  // Get measures and current energy label from assessment
  const measuresD = status === 'failure' ? [] : demandBasedAssessment?.retrofitStates || []
  const measuresC = status === 'failure' ? [] : consumptionBasedAssessment?.retrofitStates || []

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#F5F5F7',
      }}
    >
      <HamburgerMenu
        sx={{
          position: 'fixed',
          top: 32,
          left: 32,
          display: { xs: 'flex', lg: 'none' },
        }}
      />

      <Tooltip
        title={!customPackage ? 'Bitte wählen Sie ein Paket aus, um das PDF zu erstellen' : ''}
        arrow
        placement="left"
      >
        <span
          style={{
            position: 'fixed',
            top: 32,
            right: 32,
            zIndex: 1000,
            display: 'flex',
          }}
        >
          <TextButton
            disabled={!customPackage}
            onClick={handleExportPDF}
            sx={{
              height: '40px',
              paddingX: '12px',
              fontSize: '14px',
              display: { xs: 'flex', lg: 'none' },
              gap: 1,
              color: customPackage ? '#181D29' : '#A3A5A9',
            }}
          >
            <File07Icon />
            PDF erstellen
          </TextButton>
        </span>
      </Tooltip>

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100vw',
          background: 'linear-gradient(180deg, #F5F5F7 0%, #F5F5F7 90%, transparent 100%)',
          zIndex: 100,
          padding: { xs: '0 24px' },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', lg: '1125px' },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            marginTop: { xs: 10, lg: 5 },
            marginBottom: status === 'failure' ? 2 : 3,
          }}
        >
          <Box sx={{ display: { xs: 'none', lg: 'flex' } }}>
            <HamburgerMenu />
          </Box>
          <Typography
            component="h1"
            sx={{
              fontWeight: 600,
              fontSize: '28px',
              lineHeight: '42px',
              marginLeft: '8px',
            }}
          >
            <Box component="span">Aktuelle</Box>{' '}
            <Box component="span" sx={{ color: '#5BC06B' }}>
              Bewertung
            </Box>
          </Typography>

          <Tooltip
            title={!customPackage ? 'Bitte wählen Sie ein Paket aus, um das PDF zu erstellen' : ''}
            arrow
            placement="left"
          >
            <span style={{ marginLeft: 'auto', display: 'flex' }}>
              <TextButton
                disabled={!customPackage}
                onClick={handleExportPDF}
                sx={{
                  height: '40px',
                  paddingX: '12px',
                  fontSize: '14px',
                  display: { xs: 'none', lg: 'flex' },
                  gap: 1,
                  color: customPackage ? '#181D29' : '#A3A5A9',
                }}
              >
                <File07Icon />
                PDF erstellen
              </TextButton>
            </span>
          </Tooltip>
        </Box>

        {status === 'failure' && (
          <Alert
            severity="error"
            icon={<AlertCircleIcon style={{ width: 20, height: 20 }} />}
            onClick={() => setIsEditPopupOpen(true)}
            sx={{
              maxWidth: { xs: '100%', lg: '1125px' },
              width: '100%',
              cursor: 'pointer',
              marginBottom: 3,
              '&:hover': {
                backgroundColor: '#FDEDED',
              },
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
              Die Berechnung ist fehlgeschlagen. Bitte überprüfen und korrigieren Sie Ihre Eingaben.
            </Typography>
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100vw',
          backgroundColor: '#F5F5F7',
          padding: { xs: '0 24px' },
        }}
      >
        <Box sx={{ marginTop: 1, maxWidth: '800px', marginX: 'auto' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '33px',
              marginTop: '16px',
            }}
          >
            Gebäudeübersicht
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              marginTop: { xs: 2, sm: 'calc(16px + 16px)' },
              marginBottom: { sm: '24px' },
              alignItems: { sm: 'stretch' },
            }}
          >
            {/* Photo - top on mobile, right on desktop */}
            <Box
              sx={{
                order: { xs: 1, sm: 2 },
                flex: { sm: '0 0 40%' },
                maxHeight: { sm: '200px' },
                marginTop: { xs: 0, sm: '-16px' },
                marginBottom: { xs: 0, sm: '-24px' },
              }}
            >
              <PhotoUploader
                buildingUuid={buildingUuid!}
                currentImageUrl={authenticatedImageUrl}
                fallbackImageUrl={fallbackImageUrl}
              />
            </Box>

            {/* Grid - bottom on mobile, left on desktop */}
            <Box
              sx={{
                order: { xs: 2, sm: 1 },
                flex: { sm: '1' },
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
              }}
            >
              {[
                {
                  key: 'Baujahr',
                  value: buildingInfo?.constructionYear.toString() || '–',
                  icon: <HomeSmileIcon style={{ width: 16, height: 16 }} />,
                },
                {
                  key: 'Wohneinheiten',
                  value: buildingInfo?.residentialUnits.toString() || '–',
                  icon: <Cube01Icon style={{ width: 16, height: 16 }} />,
                },
                {
                  key: 'Heizung',
                  value: formatEnergySourceLabel(buildingInfo?.heatingEnergySource),
                  icon: <FireIcon width={16} height={16} />,
                },
                {
                  key: 'Warmwasser',
                  value: formatEnergySourceLabel(buildingInfo?.hotWaterEnergySource),
                  icon: <WavesIcon style={{ width: 16, height: 16 }} />,
                },
                {
                  key: 'Energieverbrauch',
                  value: annualEnergyConsumption || '–',
                  unit: 'kWh/Jahr',
                  icon: <Speedometer04Icon style={{ width: 16, height: 16 }} />,
                  trailing: (
                    <InfoCircleIcon
                      style={{ width: 18, height: 18, color: '#464A54', cursor: 'pointer' }}
                      onClick={() => setIsEnergyConsumptionInfoPopupOpen(true)}
                    />
                  ),
                },
                {
                  key: 'Energiebedarf',
                  value: annualEnergyDemand || '–',
                  unit: 'kWh/Jahr',
                  icon: <File05Icon style={{ width: 16, height: 16 }} />,
                  trailing: (
                    <InfoCircleIcon
                      style={{ width: 18, height: 18, color: '#464A54', cursor: 'pointer' }}
                      onClick={() => setIsEnergyDemandInfoPopupOpen(true)}
                    />
                  ),
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '6px',
                      backgroundColor: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#464A54',
                        fontWeight: 500,
                        fontSize: '10px',
                        lineHeight: '18px',
                      }}
                    >
                      {item.key}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      {item.value}
                      {item.unit && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: '10px',
                            fontWeight: 500,
                            marginLeft: '4px',
                          }}
                        >
                          {item.unit}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  {item.trailing}
                </Box>
              ))}
            </Box>
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '24px',
              marginTop: { xs: '20px', sm: '32px' },
            }}
          >
            Ihr Gebäude-Potenzial
          </Typography>

          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '8px',
              lineHeight: '14px',
              color: '#74777F',
            }}
          >
            Berechnet mit technisch sinnvollen Maßnahmen mit kurzer Amortisationszeit*
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: '26px',
              marginTop: '20px',
            }}
          >
            {/* Energy Efficiency Scale */}
            <Energieklasse
              currentRating={currentRating}
              potentialRating={customPackage?.data.demandBasedCalculation.energetics.energyLabel}
            />

            {/* Two boxes on the right */}
            <Box
              sx={{
                flex: { sm: '1' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '16px',
              }}
            >
              <SavingsPotential
                avgEnergyCostSavingsWithinFiveYears={{
                  inCt: customPackage?.data.consumptionBasedCalculation.economics
                    .avgEnergyCostSavingsInCtWithinFiveYears,
                  inPct:
                    customPackage?.data.consumptionBasedCalculation.economics
                      .avgEnergyCostSavingsInPctWithinFiveYears,
                }}
                annualFinalEnergyInKwh={{
                  current:
                    consumptionBasedAssessment?.currentState.energetics.annualFinalEnergyInKwh
                      .total,
                  retrofit:
                    customPackage?.data.consumptionBasedCalculation.energetics
                      .annualFinalEnergyInKwh.total,
                }}
              />
              <PropertyValueIncrease
                percentage={
                  customPackage?.data.demandBasedCalculation.economics
                    .realEstateValuationIncreaseInPct
                }
              />
            </Box>
          </Box>

          <Box sx={{ marginTop: '16px' }}>
            <ProposedMeasures
              onEmptyStateClick={() => navigate(`/${buildingUuid}/measures-overview`)}
              onRetrofitClick={handleMeasureClick}
              customPackage={customPackage}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100vw',
          background: 'linear-gradient(180deg, #F5F5F7 0px, #FFFFFF 48px)',
          padding: { xs: '0 24px' },
        }}
      >
        <Box
          sx={{
            maxWidth: '800px',
            width: '100%',
            marginX: 'auto',
            marginTop: customPackage ? '44px' : '20px',
            marginBottom: '120px',
          }}
        >
          <CurrentStateSection
            substance={buildingInfo?.substance}
            currentStateDescription={buildingInfo?.currentStateDescription}
          />
        </Box>
      </Box>

      {!customPackage && (
        <Button
          endIcon={<ArrowNarrowRightIcon />}
          onClick={handleNavigateToMeasures}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, lg: 32 },
            right: { xs: 24, lg: 32 },
          }}
        >
          Jetzt Maßnahmen-Pakete erstellen
        </Button>
      )}

      {showEndConsultationButton && (
        <Button
          onClick={handleEndConsultation}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, lg: 32 },
            right: { xs: 24, lg: 32 },
            backgroundColor: 'white',
            color: 'black',
            '&:hover': {
              backgroundColor: 'white',
              opacity: 0.9,
            },
          }}
        >
          Energieberatung beenden
        </Button>
      )}

      {/* Measure Details Popup */}
      {assessment?.data && (
        <MeasureDetailsPopup
          open={isMeasureDetailsPopupOpen}
          onClose={() => setIsMeasureDetailsPopupOpen(false)}
          measureC={measuresC.find(r => r.retrofitName === selectedMeasure) || null}
          measureD={measuresD.find(r => r.retrofitName === selectedMeasure) || null}
          assessment={assessment.data}
          financing={financing?.find(f => f.retrofitName === selectedMeasure)}
        />
      )}

      <EnergyConsumptionInfoPopup
        open={isEnergyConsumptionInfoPopupOpen}
        onClose={() => setIsEnergyConsumptionInfoPopupOpen(false)}
        onEdit={() => {
          setIsEnergyConsumptionInfoPopupOpen(false)
          setIsEditPopupOpen(true)
        }}
      />

      <EnergyDemandInfoPopup
        open={isEnergyDemandInfoPopupOpen}
        onClose={() => setIsEnergyDemandInfoPopupOpen(false)}
        onEdit={() => {
          setIsEnergyDemandInfoPopupOpen(false)
          setIsEditPopupOpen(true)
        }}
      />

      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
    </Box>
  )
}

export default CurrentRatingPage
